// IntelliAuth v2 Control-Plane (CP) API Client.
//
// Type-safe fetch wrapper backed by codegen types from
// intelliauth-backend-v2/cp/api/openapi.yaml.
//
// Why a separate package from `@intelliauth/api-client-v2`?
//   - DP and CP are distinct services with separate OpenAPI specs,
//     listeners (DP :8080 / CP :8090), and ingress hosts.
//   - The backend framework spec §94 envelopes are similar but the auth
//     flows differ (CP uses platform/org-scoped Caller, DP uses tenant
//     Caller). A shared base would force premature convergence on a
//     surface that is expected to drift.
//
// The shape mirrors V2Client (codegen-typed paths, interpolation helper,
// 401 hook, JSON-only body, credentials: 'include' for cookie auth) so
// migration of CP-side consumers is mechanical: swap the import and the
// path literals are checked against the CP spec.

import type { paths } from './types.gen'

export type Paths = paths

// Path-level type helpers ------------------------------------------------

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete'

type PathsForMethod<M extends HttpMethod> = {
  [P in keyof paths]: paths[P] extends Record<M, unknown> ? P : never
}[keyof paths]

type Op<P extends keyof paths, M extends HttpMethod> =
  paths[P] extends Record<M, infer O> ? O : never

type ResponseBody<O> = O extends {
  responses: infer R
}
  ? R extends Record<number, infer V>
    ? V extends { content: { 'application/json': infer J } }
      ? J
      : V extends { content: { [k: string]: infer J } }
        ? J
        : never
    : never
  : never

type RequestBody<O> = O extends {
  requestBody?: { content: { 'application/json': infer J } }
}
  ? J
  : O extends { requestBody: { content: { 'application/json': infer J } } }
    ? J
    : never

type PathParams<O> = O extends { parameters: { path: infer P } } ? P : never

type QueryParams<O> = O extends { parameters: { query?: infer Q } } ? Q : never

// Error mapping ----------------------------------------------------------

export class CpV2ApiError extends Error {
  status: number
  code?: string
  details?: unknown
  /**
   * End-user-safe copy from the CP error envelope (`error.user_message`).
   * Mirrors the DP `V2ApiError.userMessage` field; UI consumers should
   * prefer this over `message`.
   */
  userMessage?: string
  /** Engineering-facing copy from the CP error envelope (`error.developer_message`). */
  developerMessage?: string

  constructor(
    status: number,
    message: string,
    code?: string,
    details?: unknown,
    extras?: { userMessage?: string; developerMessage?: string },
  ) {
    super(message)
    this.name = 'CpV2ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.userMessage = extras?.userMessage
    this.developerMessage = extras?.developerMessage
  }
}

// Config -----------------------------------------------------------------

export type FetchImpl = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export interface CpV2ClientConfig {
  /**
   * Base URL for the v2 cp-api. Defaults to `http://localhost:8090` (the
   * local cp-api dev listener); override for non-local deploys, e.g.
   * `https://cp.intelliauth.test` in B5/CP-G ingress, or empty string
   * (`''`) when same-origin behind a Vite proxy.
   */
  baseUrl?: string
  /**
   * Optional callback fired on 401 AFTER any refresh attempt has failed
   * (or when no refresh callback is configured). Receives the call's
   * METHOD and PATH so the consumer can apply a route-aware policy
   * (e.g. boot session probe + anonymous endpoints don't trigger
   * logout).
   */
  onUnauthorized?: (info: { method: string; path: string }) => void

  /**
   * Optional refresh callback. When the access token expires server-
   * side and we get a 401 on a non-anonymous call, the client invokes
   * this callback ONCE. If it returns a non-empty access token, the
   * client sets it and transparently retries the original request
   * exactly once. If it returns null (or throws), the client gives up,
   * fires `onUnauthorized`, and throws `CpV2ApiError(401)` to the
   * caller as usual.
   *
   * The callback is responsible for de-duplicating concurrent refresh
   * attempts and for token rotation. CpV2Client does not cache or
   * inspect the returned token — it just sets it via setAccessToken
   * before retrying.
   *
   * Implementations MUST NOT recurse into the same CpV2Client (the
   * refresh endpoint should be reached via raw fetch or a separate
   * client instance), otherwise a 401 on /auth/refresh itself would
   * infinite-loop.
   */
  refreshAccessToken?: () => Promise<string | null>

  /**
   * Optional path-template predicate. Returns true for paths where a
   * 401 is data (logged-out probe, availability check, etc.) — NOT a
   * session-expired signal. When true, CpV2Client skips refresh AND
   * onUnauthorized, just throws CpV2ApiError(401) so the caller can
   * branch.
   *
   * Receives the UN-INTERPOLATED path template (e.g.
   * "/api/v1/invitations/{token}"), so the predicate can be a pure
   * Set lookup.
   */
  isAnonymousPath?: (path: string) => boolean
  /** Inject custom fetch (tests). Defaults to global fetch with credentials: 'include'. */
  fetchImpl?: FetchImpl
  /**
   * Optional default headers added to every request. Useful for the
   * platform-admin console where every CP write requires a bearer or
   * X-Org-Id selector header propagated from the page state.
   */
  headers?: Record<string, string>
}

const DEFAULT_BASE_URL = 'http://localhost:8090'

// Client -----------------------------------------------------------------

export class CpV2Client {
  protected baseUrl: string
  protected onUnauthorized?: (info: { method: string; path: string }) => void
  protected refreshAccessToken?: () => Promise<string | null>
  protected isAnonymousPath?: (path: string) => boolean
  protected fetchImpl: FetchImpl
  protected defaultHeaders: Record<string, string>
  /**
   * Aegis-issued ES256 JWT used for Authorization: Bearer on every
   * authenticated request. Set via setAccessToken() after a successful
   * login(); cleared by clearAccessToken() on logout. Per LD-JWT in
   * docs/superpowers/specs/2026-05-09-cp-org-console-api-gap-fill.md
   * the cp-org-console FE stores this in memory only — never in
   * localStorage (XSS) and never in a cookie (the previous transport).
   */
  protected accessToken?: string

  constructor(config: CpV2ClientConfig = {}) {
    const base = config.baseUrl ?? DEFAULT_BASE_URL
    this.baseUrl = base.replace(/\/+$/, '')
    this.onUnauthorized = config.onUnauthorized
    this.refreshAccessToken = config.refreshAccessToken
    this.isAnonymousPath = config.isAnonymousPath
    this.fetchImpl = config.fetchImpl ?? ((input, init) => fetch(input, init))
    this.defaultHeaders = config.headers ?? {}
  }

  /** Replace the in-memory access token. Subsequent requests attach
   *  it as `Authorization: Bearer <token>`. */
  setAccessToken(token: string): void {
    this.accessToken = token
  }

  /** Clear the in-memory access token. Subsequent requests are
   *  anonymous (handler will return 401 on protected routes). */
  clearAccessToken(): void {
    this.accessToken = undefined
  }

  /** Returns true when an access token is currently set. */
  hasAccessToken(): boolean {
    return !!this.accessToken
  }

  async get<P extends PathsForMethod<'get'>>(
    path: P,
    init?: {
      path?: PathParams<Op<P, 'get'>>
      query?: QueryParams<Op<P, 'get'>>
    },
  ): Promise<ResponseBody<Op<P, 'get'>>> {
    return this.request<ResponseBody<Op<P, 'get'>>>('GET', path, init?.path, init?.query)
  }

  async post<P extends PathsForMethod<'post'>>(
    path: P,
    init?: {
      path?: PathParams<Op<P, 'post'>>
      query?: QueryParams<Op<P, 'post'>>
      body?: RequestBody<Op<P, 'post'>>
    },
  ): Promise<ResponseBody<Op<P, 'post'>>> {
    return this.request<ResponseBody<Op<P, 'post'>>>(
      'POST',
      path,
      init?.path,
      init?.query,
      init?.body,
    )
  }

  async put<P extends PathsForMethod<'put'>>(
    path: P,
    init?: {
      path?: PathParams<Op<P, 'put'>>
      query?: QueryParams<Op<P, 'put'>>
      body?: RequestBody<Op<P, 'put'>>
    },
  ): Promise<ResponseBody<Op<P, 'put'>>> {
    return this.request<ResponseBody<Op<P, 'put'>>>(
      'PUT',
      path,
      init?.path,
      init?.query,
      init?.body,
    )
  }

  async patch<P extends PathsForMethod<'patch'>>(
    path: P,
    init?: {
      path?: PathParams<Op<P, 'patch'>>
      query?: QueryParams<Op<P, 'patch'>>
      body?: RequestBody<Op<P, 'patch'>>
    },
  ): Promise<ResponseBody<Op<P, 'patch'>>> {
    return this.request<ResponseBody<Op<P, 'patch'>>>(
      'PATCH',
      path,
      init?.path,
      init?.query,
      init?.body,
    )
  }

  async delete<P extends PathsForMethod<'delete'>>(
    path: P,
    init?: {
      path?: PathParams<Op<P, 'delete'>>
      query?: QueryParams<Op<P, 'delete'>>
    },
  ): Promise<ResponseBody<Op<P, 'delete'>>> {
    return this.request<ResponseBody<Op<P, 'delete'>>>('DELETE', path, init?.path, init?.query)
  }

  protected async request<T>(
    method: string,
    path: string,
    pathParams?: Record<string, string | number> | unknown,
    query?: Record<string, unknown> | unknown,
    body?: unknown,
    // _retried is set internally on the second attempt after a
    // successful refresh, to prevent infinite refresh loops if the
    // brand-new access token ALSO returns 401 (e.g. server-side
    // revocation between mint and use).
    _retried?: boolean,
  ): Promise<T> {
    const interpolated = pathParams
      ? path.replace(/\{([^}]+)\}/g, (_, k) => {
          const v = (pathParams as Record<string, string | number>)[k]
          if (v === undefined) {
            throw new CpV2ApiError(0, `missing path param: ${k}`)
          }
          return encodeURIComponent(String(v))
        })
      : path

    const qs =
      query && typeof query === 'object'
        ? '?' +
          Object.entries(query as Record<string, unknown>)
            .filter(([, v]) => v !== undefined && v !== null)
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
            .join('&')
        : ''

    const url = `${this.baseUrl}${interpolated}${qs && qs !== '?' ? qs : ''}`

    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...this.defaultHeaders,
    }
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }
    // Per LD-JWT (cp-org-console gap-fill spec): cp-api is JWT-only.
    // Attach the in-memory access token as Authorization: Bearer when
    // present; anonymous requests (login, signup) carry no token.
    // No `credentials: 'include'` — cookies are no longer the auth
    // transport.
    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`
    }

    const res = await this.fetchImpl(url, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })

    if (res.status === 401) {
      // Refresh-and-retry interceptor. The full decision tree:
      //
      //   anonymous path?      → throw (data 401, not session expiry)
      //   already retried?     → throw + fire onUnauthorized (give up)
      //   no refresh callback? → throw + fire onUnauthorized
      //   refresh returns null → throw + fire onUnauthorized
      //   refresh returns tok  → setAccessToken + retry once
      //
      // Concurrent 401s share ONE refresh promise (the FE-side
      // refreshAccessToken implementation de-duplicates), so an
      // expired-token batch becomes exactly one Aegis /sessions/refresh
      // round-trip even if 10 widgets faulted at once.
      const callerPath = path
      const anonymous = this.isAnonymousPath?.(callerPath) ?? false
      if (anonymous || _retried || !this.refreshAccessToken) {
        if (!anonymous) {
          this.onUnauthorized?.({ method, path: callerPath })
        }
        throw new CpV2ApiError(401, 'Unauthorized')
      }
      const newToken = await this.refreshAccessToken()
      if (!newToken) {
        this.onUnauthorized?.({ method, path: callerPath })
        throw new CpV2ApiError(401, 'Unauthorized')
      }
      this.setAccessToken(newToken)
      // Retry the original request once with the rotated token. The
      // _retried flag stops any subsequent 401 from triggering ANOTHER
      // refresh — that would loop forever if the freshly minted token
      // is somehow rejected too.
      return this.request<T>(method, path, pathParams, query, body, true)
    }

    if (res.status === 204) {
      return undefined as T
    }

    if (!res.ok) {
      let code: string | undefined
      let msg = `Request failed: ${res.status}`
      let details: unknown
      let userMessage: string | undefined
      let developerMessage: string | undefined
      try {
        const errBody = await res.json()
        // CP error envelope per cp/api/openapi.yaml `ErrorEnvelope` schema
        // (mirrors framework spec §94 / §101.24.2 --
        // {error: {code, user_message, developer_message?, details?}, meta}).
        // Prefer user_message (end-user-safe). Fall back to developer_message
        // for early v2 surfaces that only populate the engineering string,
        // then to the legacy `message` field, then to the HTTP-status sentinel.
        const err = (
          errBody as {
            error?: {
              code?: string
              user_message?: string
              developer_message?: string
              message?: string
              details?: unknown
            }
          }
        ).error
        if (err) {
          code = err.code
          userMessage = err.user_message
          developerMessage = err.developer_message
          msg = err.user_message ?? err.developer_message ?? err.message ?? msg
          details = err.details
        }
      } catch {
        // non-JSON error body -- keep defaults
      }
      throw new CpV2ApiError(res.status, msg, code, details, { userMessage, developerMessage })
    }

    return (await res.json()) as T
  }
}
